import type { components } from '@connected/api-client/api.generated';
import type { RadioGroupOption } from '@connected/form-fields-v2/components/form/radio-group';
import type { ColumnDef } from '@tanstack/react-table';

import { api } from '@connected/api-client';
import 'react-phone-number-input/style.css';
import { validators } from '@connected/api-client/generated/validators.generated';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { useAppForm } from '@connected/form-fields-v2/hooks/use-app-form';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Button } from '@connected/ui/components/button';
import { Checkbox } from '@connected/ui/components/checkbox';
import { Item } from '@connected/ui/components/item';
import { Popout, PopoutBody, PopoutContent, PopoutHeader, PopoutTitle } from '@connected/ui/components/popout';
import { Skeleton } from '@connected/ui/components/skeleton';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { format, parseISO } from 'date-fns';
import { ChevronDown, ChevronRight, Circle, Edit, PlusIcon, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { DataTable } from '@/components/data-table';
import { ExpandedRowsMobile } from '@/components/expanded-row';
import { LoadingPage } from '@/components/loading-page';
import { logger } from '@/lib/logger';
import { queryClient } from '@/lib/query-client';
import { showConfirm } from '@/stores/confirmer';

export const Route = createFileRoute('/_auth/dashboard/organizations/$organizationId/settings/measuring-instruments/')({
    component: RouteComponent,
});

type MeasurementDevice = {
    Name: string;
    BrandName: string;
    DeviceType: string;
    SerialNumber: string;
    CalibrationKind: string;
    UserDisplayName?: string;
    StartDate?: string;
    EndDate?: string;
    ElectricalMeasurementDeviceId: string;
    InspectionKinds: Array<'None' | 'Scope8' | 'Scope10' | 'Scope12' | 'Nen1010' | 'Nen3140'>;
};
type CreateMeasurementDevice = components['schemas']['CreateElectricalMeasurementDeviceCommand'];
type EditElectricalMeasurementDeviceCommand = components['schemas']['EditElectricalMeasurementDeviceCommand'];
function UpdateMeasurementDeviceCard({
    isOpen,
    setIsOpen,
    Measurement,
    UserIds,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    Measurement: MeasurementDevice | null;
    UserIds?: Array<{ value: string; label: string }>;
}) {
    const { t } = useTranslation('dashboard');
    const { organizationId } = Route.useParams();

    const [loading, setIsLoading] = useState<boolean>(false);

    const DEFAULT_VALUES_UPDATE = useMemo<EditElectricalMeasurementDeviceCommand>(() => ({
        ElectricalMeasurementDeviceId: Measurement?.ElectricalMeasurementDeviceId ?? '',
        UserId: '',
        Name: Measurement?.Name ?? '',
        StartDate: Measurement?.StartDate ?? '',
        EndDate: Measurement?.EndDate ?? '',
        InspectionKinds: Measurement?.InspectionKinds ?? [],
    }), [Measurement]);

    const updateCurrentUser = api.useMutation(
        'put',
        '/api/v2/electrical/measurementdevices/edit',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    {
                        queryKey: elektraAppQueryOptions.organisation.management.measurementDevicesOptions({ organisationId: organizationId }).queryKey,
                    },
                ).then(() => {
                    toast.success(t('dashboard.settings.measuring-instruments.message.updateSuccess'));
                    setIsOpen(false);
                });
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'EditElectricalMeasurementUser',
                        error,
                    },
                    { apiHttpMethod: 'Put', apiUrl: '/api/v2/electrical/measurementdevices/edit' },
                );
                toast.error(
                    t(error?.Message ?? t('dashboard.general.messages.error')),
                );
            },
            onMutate: () => setIsLoading(true),

        },
    );

    const form = useAppForm({
        defaultValues: DEFAULT_VALUES_UPDATE,
        onSubmit: (values) => {
            updateCurrentUser.mutateAsync({
                body: {
                    ElectricalMeasurementDeviceId: values.value.ElectricalMeasurementDeviceId,
                    Name: values.value.Name,
                    EndDate: values.value.EndDate,
                    StartDate: values.value.StartDate,
                    InspectionKinds: values.value.InspectionKinds,
                    UserId: values.value.UserId,
                },
            });
        },
    });

    useEffect(() => {
        if (isOpen) {
            form.reset(DEFAULT_VALUES_UPDATE);
        }
    }, [isOpen, form, DEFAULT_VALUES_UPDATE]);

    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>
                        {t('dashboard.settings.measuring-instruments.update.title')}
                    </PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit}>
                        <form.AppField
                            name="UserId"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCommand.userId,
                            }}
                            children={field => (
                                <field.Label asDiv>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.userId')} />
                                    <field.Popover closeOnChange={true}>
                                        <field.RadioGroup options={UserIds ?? []}>
                                            <field.Popover.Trigger asChild>
                                                <field.RadioGroup.Trigger />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                <field.RadioGroup options={UserIds ?? []} />
                                            </field.Popover.Content>
                                        </field.RadioGroup>
                                    </field.Popover>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.save')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

export function CreateMeasurementDeviceCard({
    isOpen,
    setIsOpen,
    UserIds,
}: {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    UserIds?: Array<{ value: string; label: string }>;
}) {
    const { t } = useTranslation('dashboard');
    const { organizationId } = Route.useParams();

    const [loading, setIsLoading] = useState<boolean>(false);

    const DEFAULT_CREATE_MEASUREMENT_DEVICE_VALUES = useMemo<CreateMeasurementDevice>(() => ({
        OrganizationId: '',
        UserId: '',
        Name: '',
        BrandName: '',
        DeviceType: '',
        SerialNumber: '',
        CalibrationKind: 'Required',
        InspectionKinds: [],
    }), []);

    const calibrationKindList: RadioGroupOption<CreateMeasurementDevice['CalibrationKind']>[] = [
        { value: 'Required', label: 'Verplicht' },
        { value: 'NotApplicable', label: 'NVT' },
        { value: 'Optional', label: 'Optioneel' },

    ];

    const createNewUser = api.useMutation(
        'post',
        '/api/v2/electrical/measurementdevices/create',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    {
                        queryKey: elektraAppQueryOptions.organisation.management.measurementDevicesOptions({ organisationId: organizationId }).queryKey,
                    },
                ).then(() => {
                    toast.success(t('dashboard.settings.measuring-instruments.message.createSuccess'));
                    setIsOpen(false);
                });
            },
            onError: (error) => {
                logger.error(
                    {
                        system: 'createNewMeasurementDevice',
                        error,
                    },
                    { apiHttpMethod: 'Post', apiUrl: '/api/v2/electrical/measurementdevices/create' },
                );
                toast.error(
                    error?.Message ?? t('dashboard.general.messages.error'),
                );
            },
            onMutate: () => setIsLoading(true),
        },
    );
    const form = useAppForm({
        defaultValues: DEFAULT_CREATE_MEASUREMENT_DEVICE_VALUES,
        onSubmit: (form) => {
            createNewUser.mutateAsync({
                body: {
                    OrganizationId: organizationId,
                    UserId: form?.value?.UserId,
                    Name: form?.value?.Name,
                    BrandName: form?.value?.BrandName,
                    DeviceType: form?.value?.DeviceType,
                    SerialNumber: form?.value?.SerialNumber,
                    CalibrationKind: form?.value?.CalibrationKind,
                    InspectionKinds: [],
                },
            });
        },
    });
    function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        form.handleSubmit();
    }
    useEffect(() => {
        if (isOpen) {
            form.reset(DEFAULT_CREATE_MEASUREMENT_DEVICE_VALUES);
        }
    }, [isOpen, form, DEFAULT_CREATE_MEASUREMENT_DEVICE_VALUES]);

    return (
        <Popout open={isOpen} onOpenChange={setIsOpen}>
            <PopoutContent>
                <PopoutHeader>
                    <PopoutTitle>{t('dashboard.settings.measuring-instruments.create.title')}</PopoutTitle>
                </PopoutHeader>
                <PopoutBody>
                    <form onSubmit={handleSubmit} className="flex flex-col">
                        <form.AppField
                            name="Name"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCommand.name,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.name')} />
                                    <field.Input
                                        placeholder={t('dashboard.settings.measuring-instruments.create.namePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="BrandName"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCommand.brandName,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.brandName')} />
                                    <field.Input
                                        placeholder={t('dashboard.settings.measuring-instruments.create.brandNamePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="DeviceType"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCommand.deviceType,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.deviceType')} />
                                    <field.Input
                                        placeholder={t('dashboard.settings.measuring-instruments.create.deviceTypePlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="SerialNumber"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCommand.serialNumber,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.serialNumber')} />
                                    <field.Input
                                        placeholder={t('dashboard.settings.measuring-instruments.create.serialNumberPlaceholder')}
                                    />
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>

                        <form.AppField
                            name="UserId"
                            validators={{
                                onChange: validators.createElectricalMeasurementDeviceCommand.userId,
                            }}
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.userId')} />
                                    <field.Popover closeOnChange={true}>
                                        <field.RadioGroup options={UserIds ?? []}>
                                            <field.Popover.Trigger asChild>
                                                <field.RadioGroup.Trigger />
                                            </field.Popover.Trigger>
                                            <field.Popover.Content className="h-auto min-h-30 [&>div]:flex-col [&>div]:items-start [&>div]:gap-6" matchTriggerWidth>
                                                <field.RadioGroup options={UserIds ?? []} />
                                            </field.Popover.Content>
                                        </field.RadioGroup>
                                    </field.Popover>
                                    <field.ValidationError />
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <form.AppField
                            name="CalibrationKind"
                            children={field => (
                                <field.Label>
                                    <field.Label.Title label={t('dashboard.settings.measuring-instruments.create.calibrationKind')} />
                                    <field.RadioGroup
                                        orientation="horizontal"
                                        options={calibrationKindList}
                                        // TODO: refactor to form fields component
                                        renderItem={item => (
                                            <Item
                                                variant="outline"
                                                onClick={() => item.onSelect?.(item.option.value as 'Required' | 'Optional' | 'NotApplicable')}
                                                className="flex-1 border-t text-sm flex-row border-b hover:cursor-pointer border-r-transparent rounded-none p-2 m-0 justify-center first:rounded-s-md last:rounded-e-md last:border-r-border"
                                            >
                                                <Item.Content>
                                                    <Item.Title>{item.option.label}</Item.Title>
                                                    {item.option?.description && <Item.Description>{item.option.description}</Item.Description>}

                                                </Item.Content>
                                                <Item.Content>
                                                    <Checkbox
                                                        checked={item?.selected}

                                                        className="rounded-3xl p-2 !!bg-brand-solid"
                                                        icon={<Circle fill="white" className="absolute" size={6} />}
                                                    />
                                                </Item.Content>
                                            </Item>
                                        )}
                                    >
                                    </field.RadioGroup>
                                </field.Label>
                            )}
                        >
                        </form.AppField>
                        <div className="w-full flex flex-row gap-2 mt-5">
                            <Button className="w-full" onClick={() => setIsOpen(false)} variant="outline">{t('dashboard.cancel')}</Button>
                            <Button disabled={loading} type="submit" variant="default" className="w-full">{t('dashboard.create')}</Button>
                        </div>
                    </form>
                </PopoutBody>
            </PopoutContent>
        </Popout>
    );
}

function RouteComponent() {
    const { t } = useTranslation('dashboard');
    const { organizationId } = Route.useParams();
    const search = Route.useSearch() as any;
    const navigate = useNavigate();
    const [isCreateMeasurementOpen, setCreateMeasurementOpen] = useState(false);
    const [isEditMeasurementOpen, setEditMeasurementOpen] = useState(false);
    const [selectedMeasurement, setSelectedMeasurement] = useState<MeasurementDevice | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);

    const measurementDevicesOptions = useMemo(
        () => elektraAppQueryOptions.organisation.management.measurementDevicesOptions({
            organisationId: organizationId,
        }),
        [organizationId],
    );

    const { data, isLoading, isError, error } = useQuery(measurementDevicesOptions);

    const userQueryOptions = useMemo(
        () => portaalQueryOptions.users.userIdsOptions({
            organisationId: organizationId,
        }),
        [organizationId],
    );

    const { data: users, isError: isUserError, error: userErrors, isLoading: isloadingUser } = useQuery(userQueryOptions);

    if (isUserError) {
        logger.error(
            {
                system: 'ListUserQueryResult',
                userErrors,
            },
            { apiHttpMethod: 'Get', apiUrl: '/api/v2/users/list' },
        );
        toast.error(isUserError ?? t('dashboard.general.messages.error'));
    }

    const UserIds = (users?.Users ?? []).map(row => ({
        value: row?.UserId ?? '',
        label: row?.DisplayName ?? '',
    }));

    const deleteMeasurementDevice = api.useMutation(
        'delete',
        '/api/v2/electrical/measurementdevices/delete',
        {
            onSuccess: () => {
                queryClient.invalidateQueries(
                    {
                        queryKey: elektraAppQueryOptions.organisation.management.measurementDevicesOptions({ organisationId: organizationId }).queryKey,
                    },
                ).then(() => {
                    toast.success(t('dashboard.settings.measuring-instruments.message.removeSuccess'));
                });
            },
        },
    );

    const columns = useMemo<ColumnDef<MeasurementDevice>[]>(() => ([
        {
            accessorKey: 'Name',
            meta: {
                sticky: false,
                width: 300,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.name')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell variant="bold">
                    <Button
                        variant="ghost"
                        className="sm:hidden mr-2"
                        onClick={() => setExpandedId(prev => prev === row?.original?.ElectricalMeasurementDeviceId ? null : row?.original?.ElectricalMeasurementDeviceId ?? null)}
                    >
                        {expandedId === row?.original?.ElectricalMeasurementDeviceId ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </Button>
                    <div>
                        {row.original.Name || '-'}
                        <br></br>
                        <span className="text-text-primary/40">
                            {row?.original.UserDisplayName
                                ? (
                                        <>
                                            {t('dashboard.settings.measuring-instruments.table.belong')}
                                            {' '}
                                            {row?.original?.UserDisplayName}
                                        </>
                                    )
                                : null}
                        </span>
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'BrandType',
            meta: {
                sticky: false,
                width: 200,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.brandType')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell>
                    {`${row.original?.BrandName || '-'} ${row.original?.DeviceType}`}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'SerialNumber',
            meta: {
                sticky: false,
                width: 200,
                mobileHidden: true,
            },
            header: () => (
                <DataTable.Header>
                    {t('dashboard.settings.measuring-instruments.table.serialNumber')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell>
                    {row.original?.SerialNumber || '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'StartDate',
            meta: {
                sticky: false,
                width: 200,
                mobileHidden: true,
            },
            header: ({ column }) => (
                <DataTable.Header variant="sort" column={column}>
                    {t('dashboard.settings.measuring-instruments.table.startDate')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell>
                    {row.original?.StartDate
                        ? format(parseISO(row.original.StartDate), 'dd-MM-yy')
                        : '-'}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'EndDate',
            meta: {
                sticky: false,
                width: 200,
                mobileHidden: true,
            },
            header: ({ column }) => (
                <DataTable.Header
                    variant="sort"
                    column={column}
                >
                    {t('dashboard.settings.measuring-instruments.table.endDate')}
                </DataTable.Header>
            ),
            cell: ({ row }) => (
                <DataTable.Cell>
                    {row.original?.EndDate
                        ? format(parseISO(row.original.EndDate), 'dd-MM-yy')
                        : '-'}
                </DataTable.Cell>
            ),
        },
        {
            id: 'actionModal',
            meta: {
                sticky: true,
                width: 10,
            },
            header: '',
            cell: ({ row }) => {
                return (
                    <div className="flex gap-1 flex-col sm:flex-row items-center justify-end ml-auto">
                        <Button
                            variant="secondary"
                            size="icon"
                            className="flex gap-2 underline w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                            onClick={() => {
                                navigate({
                                    to: '/dashboard/organizations/$organizationId/settings/measuring-instruments/$measurementDeviceId/calibrations',
                                    params: {
                                        organizationId,
                                        measurementDeviceId: row.original?.ElectricalMeasurementDeviceId,
                                    },
                                });
                            }}
                        >
                            {t('dashboard.settings.measuring-instruments.table.calibrations')}
                        </Button>
                        <div className="flex flex-row sm:flex-col">
                            {' '}
                            <Button
                                variant="secondary"
                                size="icon"
                                className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                                onClick={async () => {
                                    if (await showConfirm(t('dashboard.settings.measuring-instruments.message.deleteMessage'))) {
                                        await deleteMeasurementDevice.mutateAsync({
                                            params: {
                                                query: {
                                                    ElectricalMeasurementDeviceId: row.original?.ElectricalMeasurementDeviceId,
                                                },
                                            },
                                        });
                                    }
                                }}
                            >
                                <Trash2 className="text-text-primary/40 size-5" />
                            </Button>
                            <Button
                                variant="secondary"
                                size="icon"
                                className="flex gap-2 w-fit h-fit justify-start p-2 bg-background-primary text-foreground hover:bg-background-secondary"
                                onClick={() => {
                                    setEditMeasurementOpen(true);
                                    setCreateMeasurementOpen(false);
                                    setSelectedMeasurement(row.original);
                                }}
                            >
                                <Edit className="text-text-primary/40 size-5" />
                            </Button>

                        </div>
                    </div>
                );
            },
        },
    ]), [t, expandedId, navigate, organizationId, deleteMeasurementDevice]);

    const tableColumns = useMemo(
        () => isLoading
            ? columns.map(column => ({ ...column, cell: () => <Skeleton className="h-8 w-full" /> }))
            : columns,
        [columns, isLoading],
    );

    const tableData = useMemo<MeasurementDevice[]>(
        () => {
            if (isError) {
                toast.error(error?.Message);
                return [];
            }

            const searchTerm = search.search?.toLowerCase() || '';
            const filteredDevices = (data?.Devices ?? [])
                .filter((device) => {
                    if (!searchTerm)
                        return true;
                    return [
                        device?.BrandName || '',
                        device?.Name || '',
                        device?.SerialNumber || '',
                        device?.DeviceType || '',
                        device?.CalibrationKind || '',
                        device?.UserDisplayName || '',
                    ].some(field => field?.toString().toLowerCase().includes(searchTerm));
                })
                .map(device => ({
                    Name: device?.Name ?? '',
                    BrandName: device?.BrandName ?? '',
                    DeviceType: device?.DeviceType ?? '',
                    SerialNumber: device?.SerialNumber ?? '',
                    CalibrationKind: device?.CalibrationKind ?? '',
                    UserDisplayName: device?.UserDisplayName ?? '',
                    StartDate: device?.StartDate ?? '',
                    EndDate: device?.EndDate ?? '',
                    ElectricalMeasurementDeviceId: device?.ElectricalMeasurementDeviceId ?? '',
                    InspectionKinds: device?.InspectionKinds ?? [],
                }));

            return filteredDevices;
        },
        [isError, search?.search, data?.Devices, error?.Message],
    );

    if (isLoading || isloadingUser) {
        return <LoadingPage />;
    }

    return (
        <div className="flex flex-col md:flex-wrap gap-8 justify-start">
            <div className="w-full">
                <DataTable.TableHead
                    title={t('dashboard.settings.measuring-instruments.title')}
                    subtitle={t('dashboard.settings.measuring-instruments.subtitle')}
                    buttons={[
                        {
                            name: t('dashboard.settings.measuring-instruments.title'),
                            icon: <PlusIcon size={19} />,
                            callback: () => setCreateMeasurementOpen(true),
                        },
                    ]}
                    search={Route}
                />
                <DataTable
                    className="rounded-tl-none rounded-tr-none"
                    columns={tableColumns}
                    data={tableData}
                    pageSize={10}
                    expandedRow={row =>
                        expandedId && expandedId === row?.original?.ElectricalMeasurementDeviceId
                            ? (
                                    <ExpandedRowsMobile columns={columns} data={row.original} />
                                )
                            : null}
                />
            </div>
            {
                isCreateMeasurementOpen
                    ? (
                            <CreateMeasurementDeviceCard isOpen={isCreateMeasurementOpen} setIsOpen={setCreateMeasurementOpen} UserIds={UserIds} />

                        )
                    : null
            }

            {
                isEditMeasurementOpen
                    ? (
                            <UpdateMeasurementDeviceCard
                                isOpen={isEditMeasurementOpen}
                                setIsOpen={setEditMeasurementOpen}
                                Measurement={selectedMeasurement}
                                UserIds={UserIds}
                            />
                        )
                    : null
            }

        </div>
    );
}
